import {
  Grid,
  Skeleton,
  Text,
  TextHighlight,
  TextSize,
  TextWeight
} from '@focaldata/cin-ui-components'
import useAudienceValidation from '../../../hooks/audience/useAudienceValidation'
import useGetDraftAudience from '../../../hooks/audience/useGetDraftAudience'
import useQuestionnaireValidation from '../../../hooks/questionnaire/useQuestionnaireValidation'
import { useIsAudienceReadOnly } from '../../../hooks/useIsAudienceReadOnly'
import usePollIncidenceRate from '../../../hooks/usePollIncidenceRate'
import {
  checkIsConfirmationPage,
  checkIsQuestionnairePage,
  getDaysSuffix
} from '../../../utils/HelperFunctions'
import useTranslationsCompletenessValidation from '../../Translations/hooks/useTranslationsCompletenessValidation'
import AudienceErrorPanel from './AudienceErrorPanel'
import QuestionnaireErrorPanel from './QuestionnaireErrorPanel'
import useStyles from './SurveyStats.styles'
import TranslationsErrorPanel from './TranslationsErrorPanel'

const getEstimatedDeliveryTimeText = (
  estimatedDeliveryTime?: number | null
) => {
  return `${estimatedDeliveryTime !== null ? estimatedDeliveryTime : '14+'} ${
    estimatedDeliveryTime !== null && estimatedDeliveryTime !== undefined
      ? getDaysSuffix(estimatedDeliveryTime)
      : 'days'
  }`
}
export const SurveyErrorPanel = () => {
  const { classes } = useStyles()
  const { isReadOnly } = useIsAudienceReadOnly()

  const { hasUnclearedValidationErrors } = useQuestionnaireValidation()
  const { invalidQuestionnaire } = hasUnclearedValidationErrors()

  const { draftAudience } = useGetDraftAudience()

  const { isAudienceInvalid } = useAudienceValidation({
    validationErrors: draftAudience?.validationErrors
  })

  const { getFeasibility } = usePollIncidenceRate()
  const {
    isFieldworkFeasible,
    estimatedDeliveryTime,
    isDonePolling,
    hasSupplierError
  } = getFeasibility()

  // Surfaces every allocated non-master language that's below 100% translated.
  // Unlike the audience-error panel (which is suppressed on the Confirmation
  // page today), incomplete translations are shown everywhere the survey
  // chrome appears — the researcher needs to see this whichever step they're
  // on, since the gate also blocks launch from the Confirmation page itself.
  const { incompleteLanguages, hasIncompleteTranslations } =
    useTranslationsCompletenessValidation()

  // A supplier error never reports pollingStatus=Done, so treat it as "settled"
  // too — otherwise the panel would spin on a skeleton forever instead of
  // surfacing the error.
  const loading = !isDonePolling && !hasSupplierError
  const showInvalidQuestionnairePanel =
    invalidQuestionnaire && checkIsQuestionnairePage()
  const showAudienceErrorInfoPanel =
    isAudienceInvalid && !checkIsConfirmationPage()
  const showTranslationsErrorPanel = hasIncompleteTranslations
  const validInfoPanel =
    !showAudienceErrorInfoPanel &&
    !showInvalidQuestionnairePanel &&
    !showTranslationsErrorPanel &&
    !hasSupplierError &&
    isFieldworkFeasible

  let errorPanelToShow = null

  if (!loading && !validInfoPanel) {
    const getErrorText = (): string => {
      if (showInvalidQuestionnairePanel) {
        return 'INVALID QUESTIONNAIRE'
      }
      if (showAudienceErrorInfoPanel) {
        return 'INVALID AUDIENCE'
      }
      // A supplier/API error must be reported before the plain infeasible
      // branch, otherwise a failed estimate reads as a real "infeasible" verdict.
      if (hasSupplierError) {
        return 'FEASIBILITY UNAVAILABLE'
      }
      if (!isFieldworkFeasible) {
        return 'INFEASIBLE AUDIENCE'
      }

      return ''
    }
    const errorText = getErrorText()

    // audience error panel will show on audience, questionnaire and confirmation pages
    errorPanelToShow = (
      <AudienceErrorPanel
        text={errorText}
        invalidAudience={isAudienceInvalid}
        isFeasible={isFieldworkFeasible}
        supplierError={hasSupplierError}
        estimatedDeliveryInDays={estimatedDeliveryTime}
      />
    )

    // questionnaire error panel will take precedence over audience error panel on questionnaire and confirmation pages
    if (showInvalidQuestionnairePanel) {
      errorPanelToShow = <QuestionnaireErrorPanel text={errorText} />
    }

    // Translations errors take precedence over both audience-validity and
    // feasibility messages — fixing those won't help if the survey still
    // can't actually serve the respondent's chosen language. Questionnaire
    // errors still win, because a broken questionnaire would render the
    // translations work invisible anyway.
    if (showTranslationsErrorPanel && !showInvalidQuestionnairePanel) {
      errorPanelToShow = (
        <TranslationsErrorPanel incompleteLanguages={incompleteLanguages} />
      )
    }
  }

  return (
    <Grid
      container
      direction="row"
      alignItems="center"
      className={classes.rightChild}
    >
      <Grid item xs="auto">
        {loading && <Skeleton width={50} />}
        {errorPanelToShow ||
          (!checkIsConfirmationPage() && !isReadOnly && (
            <Text
              data-testid="estimated-delivery"
              size={TextSize.ms}
              weight={TextWeight.SemiBold}
              highlight={TextHighlight.Standard}
            >
              {getEstimatedDeliveryTimeText(estimatedDeliveryTime)}
            </Text>
          ))}
      </Grid>
    </Grid>
  )
}
